import { useMemo } from 'react';
import { useAppSelector } from '../../hooks/store-hooks';
import { useFlightThresholds } from '../../hooks/use-flight-thresholds';
import CustomPopover from '../layout/CustomPopover';
import { aircraftThresholdTypeToAirportIds, AirportThresholdsNested, isThresholdAirportAvailable } from './AirportThresholdsNested';
import { BreachedThresholdValueCell } from './BreachedThresholdValueCell';
import { MonitorStatusBadge } from './MonitorStatusBadge';

export function FlightDetailsThresholdList(props: { flightId: number; popoverZIndex?: number }) {
    const thresholds = useFlightThresholds(props.flightId);

    const displayOrder = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.displayOrder);
    const definitions = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.definitions);
    const toDisplay = useMemo(() => {
        return displayOrder
            .filter((x) => !definitions[x].disabled && !definitions[x].alertsOnly)
            .map((x) => ({ type: x, info: definitions[x] }));
    }, [displayOrder, definitions]);

    const displayTypes = useMemo(
        () => toDisplay.filter((d) => thresholds?.thresholds?.find((x) => x.type === d.type)?.breachedLevel != null),
        [toDisplay, thresholds]
    );

    if (!thresholds) {
        return <></>;
    }

    return (
        <table className="cobalt-table cobalt-alternate w-100" style={{ fontSize: '0.8rem' }}>
            <thead>
                <tr>
                    <th className="ps-2">Name</th>
                    <th>Actual</th>
                    <th>Threshold</th>
                </tr>
            </thead>
            <tbody>
                {displayTypes.map((threshold) => {
                    const item = thresholds.thresholds.find((x) => x.type === threshold.type);
                    return (
                        <tr key={threshold.type}>
                            <td className="ps-2">{threshold.info.name}</td>
                            <td>{item?.actualValue || '-'}</td>
                            <td>
                                {isThresholdAirportAvailable(threshold.type, thresholds) ? (
                                    <CustomPopover
                                        closeOnClick={false}
                                        placement="right-start"
                                        zIndex={props.popoverZIndex}
                                        trigger={
                                            <MonitorStatusBadge monitorStatus={item?.monitorStatus}>
                                                {item ? (
                                                    <BreachedThresholdValueCell info={threshold.info} level={item.breachedLevel} />
                                                ) : (
                                                    '-'
                                                )}
                                            </MonitorStatusBadge>
                                        }>
                                        <div style={{ fontSize: '0.8rem' }}>
                                            <AirportThresholdsNested
                                                forceOnlyBreached
                                                airports={aircraftThresholdTypeToAirportIds(threshold.type, thresholds)}
                                                thresholdType={threshold.type}
                                            />
                                        </div>
                                    </CustomPopover>
                                ) : (
                                    <MonitorStatusBadge monitorStatus={item?.monitorStatus}>
                                        {item ? <BreachedThresholdValueCell info={threshold.info} level={item.breachedLevel} /> : '-'}
                                    </MonitorStatusBadge>
                                )}
                            </td>
                        </tr>
                    );
                })}

                {displayTypes.length === 0 && (
                    <tr>
                        <td>No breached thresholds.</td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}
